import { api } from './api';

/**
 * The site's running BMP log — the City of Austin "required details"
 * (JIM-0919-09, reshaped 2026-09-29 as an event history, JIM-0929-01). One
 * row per BMP; what happened to it is recorded as taps on the report that
 * saw it, dated by that report, and printed newest first on the last page
 * for sites flagged TX-Austin.
 */
/** JIM-0929-01: what an inspector can record about a BMP on a visit. */
export const BMP_ACTIONS: { action: BmpAction; label: string }[] = [
  { action: 'installed', label: 'Installed' },
  { action: 'needs_repair', label: 'Needs repair' },
  { action: 'repaired', label: 'Repaired' },
  { action: 'reinstalled', label: 'Reinstalled' },
  { action: 'removed', label: 'Removed' },
];
export type BmpAction =
  | 'installed'
  | 'needs_repair'
  | 'repaired'
  | 'reinstalled'
  | 'removed';

export type BmpLogEvent = {
  id: string;
  action: BmpAction | string;
  label: string;
  event_date: string; // ISO date
  inspection_id: string | null;
  note: string | null;
};

export type BmpLogEntry = {
  id: string;
  site_id: string;
  bmp_description: string;
  /** The condition line as the last filed report left it. */
  detail: string | null;
  /** Newest first. */
  events: BmpLogEvent[];
  current_action: string | null;
  current_date: string | null;
  /** On a sent report already: stays in the log, cannot be removed. */
  on_sent_report: boolean;
  // Legacy free-text dates: read on old rows, never written by the editor.
  install_date: string | null;
  repair_dates: string | null;
  removal_date: string | null;
  condition: string | null;
};

export type BmpLogEntryInput = {
  bmp_description: string;
  detail?: string | null;
};

/** This visit's one choice for a BMP; it becomes an event dated by the report. */
export type BmpTap = { entry_id: string; action: BmpAction };

/**
 * The condition lines changed during this visit, by BMP id. Like the taps,
 * they are filed with the report and saved nowhere before it.
 */
export type BmpConditions = Record<string, string>;

export function conditionsPayload(
  conditions: BmpConditions
): { entry_id: string; detail: string }[] {
  return Object.entries(conditions).map(([entry_id, detail]) => ({
    entry_id,
    detail,
  }));
}

/** The site tag that turns the Austin page and editor on. */
export const AUSTIN_JURISDICTION = 'TX-Austin';

export function isAustinSite(site: unknown): boolean {
  const tag = (site as { jurisdiction?: string | null } | null | undefined)
    ?.jurisdiction;
  return (tag ?? null) === AUSTIN_JURISDICTION;
}

export const bmpLogService = {
  async list(siteId: string): Promise<BmpLogEntry[]> {
    const r = await api.get<BmpLogEntry[]>(`/sites/${siteId}/bmp-log`);
    return r.data;
  },
  async add(siteId: string, input: BmpLogEntryInput): Promise<BmpLogEntry> {
    const r = await api.post<BmpLogEntry>(`/sites/${siteId}/bmp-log`, input);
    return r.data;
  },
  async update(
    siteId: string,
    entryId: string,
    patch: Partial<BmpLogEntryInput>
  ): Promise<BmpLogEntry> {
    const r = await api.patch<BmpLogEntry>(
      `/sites/${siteId}/bmp-log/${entryId}`,
      patch
    );
    return r.data;
  },
  async remove(siteId: string, entryId: string): Promise<void> {
    await api.delete(`/sites/${siteId}/bmp-log/${entryId}`);
  },
};
